import { decryptSecret } from "~/utils/crypto"
import { buildSignatureHeaders } from "~/utils/signature"

// 前端内置 Bootstrap Token（与后端保持一致）
const BOOTSTRAP_TOKEN = "4xH7LpQ8KjF2aR9cVbN3mW6yT1uE5iA0sD8fG7hJ9kL2zXLipSync"

/**
 * 前端 Secret 管理器
 * 负责存储当前 secret，处理响应中的新 secret
 */
type HeaderLike = Headers | Record<string, string | number | undefined> | null | undefined

function readHeader(headers: HeaderLike, key: string): string {
    if (!headers) return ""
    const getter = (headers as { get?: (name: string) => unknown }).get
    if (typeof getter === "function") {
        const value = getter.call(headers, key) || getter.call(headers, key.toLowerCase())
        return value == null ? "" : String(value)
    }
    const lowerKey = key.toLowerCase()
    const matchedKey = Object.keys(headers).find((k) => k.toLowerCase() === lowerKey)
    if (!matchedKey) return ""
    const value = headers[matchedKey]
    return value == null ? "" : String(value)
}

export const useSecretManager = () => {
    // 使用 Nuxt useState 保证跨组件共享、SSR 安全
    const currentSecret  = useState<string | null>("auth_secret", () => null)
    const secretExpireAt = useState<number>("auth_secret_expire", () => 0)

    /**
     * 解析响应 Header，如有新 secret 则更新
     */
    async function handleResponseHeaders(headers: HeaderLike): Promise<void> {
        const nextSecretEncrypted = readHeader(headers, "X-Next-Secret")
        const expireAtStr = readHeader(headers, "X-Secret-Expire")

        if (nextSecretEncrypted && expireAtStr) {
            currentSecret.value  = await decryptSecret(nextSecretEncrypted)
            secretExpireAt.value = parseInt(expireAtStr, 10)
        }
    }

    /**
     * 返回当前请求应携带的鉴权 Headers
     * - 无 secret（首次）：返回 Bootstrap Token（仅适用于换钥列表路径；访问黑名单接口前须已有 secret）
     * - 有 secret：返回 HMAC 签名 Headers
     *
     * @param identityId  当前用户 ID，匿名时传 "" 或不传
     */
    async function getAuthHeaders(
        method: string,
        path: string,
        body: string = "",
        identityId: string = ""
    ): Promise<Record<string, string>> {
        if (!currentSecret.value) {
            // 首次：用 Bootstrap Token 换 secret（目标 URL 须为换钥列表路径）
            return { "X-Bootstrap-Token": BOOTSTRAP_TOKEN }
        }

        const sigHeaders = await buildSignatureHeaders(
            currentSecret.value, method, path, body, identityId
        )

        // 有 identityId 时，同时把它放入 Header（后端从 Header 读取）
        return identityId
            ? { ...sigHeaders, "X-Identity-Id": identityId }
            : sigHeaders
    }

    return { currentSecret, secretExpireAt, handleResponseHeaders, getAuthHeaders }
}
